/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect } from "react";

import {
  CheckboxVisibility,
  CommandBar,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  ICommandBarItemProps,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";

import {
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
  DetailsListWrapper,
  TableTextWrapper,
} from "Components/Common/SharedTableStyles.styles";
import ProfileBenefitOtherTenant from "Components/ProfileBenefitOtherTenant/ProfileBenefitOtherTenant";
import { BenefitDisplayNameEnum } from "Constants/BenefitConstants";
import { IProfileAzureBenefitDetail } from "Interfaces/Benefits/IProfileAzureBenefitDetail";

import { IProfileAzureBenefitDetailProps } from "./IProfileAzureBenefitDetailProps";
import { commandBarStyles, copyIconStyles } from "./ProfileAzureBenefitDetail.styles";
import CopyButton from "Components/CopyButton/CopyButton";

const VisualStudioBenefitDetail: React.FunctionComponent<IProfileAzureBenefitDetailProps> = (
  props: IProfileAzureBenefitDetailProps
) => {
  const {
    benefitType,
    benefits,
    canManageBenefits,
    extendBenefitClicked,
    benefitOtherTenantName,
    addAzureBenefitClicked,
  } = props;

  const items: ICommandBarItemProps[] = [
    {
      key: "extendCredit",
      text: "Extend Credit",
      iconProps: { iconName: "Add" },
      onClick: (): void => {
        extendBenefitClicked(benefitType);
      },
    },
    {
      key: "addRecord",
      text: "Add a record",
      iconProps: { iconName: "Add" },
      onClick: (): void => {
        addAzureBenefitClicked(benefitType);
      },
    },
  ];

  const columns = [
    {
      key: "registrationId",
      name: "Registration ID",
      fieldName: "registrationId",
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: "startDate",
      name: "Activation Date",
      fieldName: "startDate",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: "capCredit",
      name: "Assigned Credits",
      fieldName: "capCredit",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: "expirationDate",
      name: "Credit Expiration Date",
      fieldName: "expirationDate",
      minWidth: 100,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: "email",
      name: "Email",
      fieldName: "email",
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: "isModernSponsorship",
      name: "Is Modern Sponsorship",
      fieldName: "isModernSponsorship",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
    },
  ];

  const renderItemColumn = (
    item: IProfileAzureBenefitDetail,
    index?: number,
    column?: IColumn
  ): JSX.Element => {
    switch (column?.key) {
      //add a copy button to the registration id value to clipboard
      case "registrationId":
        return (
          <Stack horizontal verticalAlign="center">
            <span>{item.registrationId}</span>
            <CopyButton styles={copyIconStyles} text={item.registrationId} showCopyText={true} />
          </Stack>
        );
      case "capCredits":
        return <span>${item.capCredit}</span>;
      case "startDate":
        if (item.startDate === null) {
          return <span></span>;
        }

        const startDate = new Date(item.startDate).toDateString();
        return <span>{startDate}</span>;
      case "expirationDate":
        if (item.expirationDate === null) {
          return <span></span>;
        }

        const expirationDate = new Date(item.expirationDate).toDateString();
        return <span>{expirationDate}</span>;
      case "isModernSponsorship":
        return <span>{item.isModernSponsorship ? "Yes" : "No"}</span>;
      default:
        const fieldContent = item[column?.fieldName as keyof IProfileAzureBenefitDetail] as string;
        return <span>{fieldContent}</span>;
    }
  };

  return (
    <ContainerWrapper>
      <Stack styles={headerStackStyles}>
        <Text styles={headerTextStyles}>{BenefitDisplayNameEnum.Azure}</Text>
      </Stack>
      {benefitOtherTenantName ? (
        <ProfileBenefitOtherTenant tenantName={benefitOtherTenantName} />
      ) : (
        <>
          {canManageBenefits && !!benefits.length && (
            <StackItem>
              <CommandBar styles={commandBarStyles} items={items} ariaLabel="Benefit actions" />
            </StackItem>
          )}
          {canManageBenefits && !benefits.length && (
            <StackItem>
              <CommandBar
                styles={commandBarStyles}
                items={[items[1]]}
                ariaLabel="Benefit actions"
              />
            </StackItem>
          )}
          <DetailsListWrapper>
            <DetailsList
              items={benefits}
              columns={columns}
              setKey="set"
              layoutMode={DetailsListLayoutMode.justified}
              ariaLabelForSelectionColumn="Toggle selection"
              ariaLabelForSelectAllCheckbox="Toggle selection for all items"
              checkButtonAriaLabel="select row"
              checkboxVisibility={CheckboxVisibility.hidden}
              onRenderItemColumn={renderItemColumn}
            />
          </DetailsListWrapper>
          {benefits && benefits.length < 1 && (
            <TableTextWrapper>No {BenefitDisplayNameEnum.Azure} for this user.</TableTextWrapper>
          )}
        </>
      )}
    </ContainerWrapper>
  );
};

export default VisualStudioBenefitDetail;
